import React, { PureComponent } from "react";

import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import paginationFactory from "react-bootstrap-table2-paginator";
import {
  selectFormat,
  sortNumeric,
  Error,
  getMetricForColumn,
} from "components/Utils/Utils.js";

// Footer row styles
const footerStyles = {
  row: {
    padding: "4px 0",
  },
  totalRow: {
    padding: "4px 0",
    fontWeight: "bold",
  },
  container: {
    backgroundColor: "#f8f9fa",
  },
};

class AdsDataByNetwork extends PureComponent {
  componentDidMount() {
    this.props.childRef(this);
  }

  renderTable(data, cells) {
    const { title } = this.props;

    const selected_columns = [];

    // add unique ids
    data.forEach((d, i) => (d.id = i));

    // Support both name and source for total row identification
    const total_index = data.findIndex(
      (row) => row.name === "Total" || row.source === "Total"
    );
    let table_data = data.slice(0);

    // Extract footer rows
    const footerRows = [];

    // Extract Total row
    const totalRow = table_data.splice(total_index, 1)[0];
    footerRows.push(totalRow);

    // Extract any additional custom footer rows (e.g. This Day in 2024)
    const customFooterRows = table_data.filter(
      (row) =>
        row.isCustomFooter ||
        row.source === "This Day in 2024" ||
        row.name === "This Day in 2024"
    );

    // Remove custom footer rows from table_data
    table_data = table_data.filter(
      (row) =>
        !row.isCustomFooter &&
        row.source !== "This Day in 2024" &&
        row.name !== "This Day in 2024"
    );

    // Add custom footer rows to footerRows array
    footerRows.push(...customFooterRows);

    const columns = [
      {
        dataField: "source",
        text: "Source",
        style: { width: "150px", minWidth: "150px", textAlign: "left" },
        headerAlign: "left",
        sort: true,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {row["source"]}
            </div>
          ));
        },
      },
      {
        dataField: "name",
        text: "Name",
        style: { width: "150px", minWidth: "150px", textAlign: "left" },
        headerAlign: "left",
        sort: true,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {row["name"]}
            </div>
          ));
        },
      },
      {
        dataField: "country",
        text: "Country",
        style: { width: "150px", minWidth: "150px", textAlign: "left" },
        headerAlign: "left",
        sort: true,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {row["country"]}
            </div>
          ));
        },
      },
      {
        dataField: "impressions",
        text: "IMPs",
        style: { width: "100px", minWidth: "100px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(row["impressions"])}
            </div>
          ));
        },
      },
      {
        dataField: "clicks",
        text: "Clicks",
        style: { width: "120px", minWidth: "120px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(
                row["clicks"],
                null,
                null,
                row.formatExtraData
              )}
            </div>
          ));
        },
      },
      {
        dataField: "views",
        text: "Views",
        style: { width: "120px", minWidth: "120px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(
                row["views"],
                null,
                null,
                row.formatExtraData
              )}
            </div>
          ));
        },
      },
      {
        dataField: "sign_ups",
        text: "Sign Ups",
        style: { width: "100px", minWidth: "100px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(
                row["sign_ups"],
                null,
                null,
                row.formatExtraData
              )}
            </div>
          ));
        },
      },
      {
        dataField: "purchases",
        text: "Purchases",
        style: { width: "100px", minWidth: "100px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(
                row["purchases"],
                null,
                null,
                row.formatExtraData
              )}
            </div>
          ));
        },
      },
      {
        dataField: "landing_page_views",
        text: "Landing Page Views",
        style: { width: "150px", minWidth: "150px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(
                row["landing_page_views"],
                null,
                null,
                row.formatExtraData
              )}
            </div>
          ));
        },
      },
      {
        dataField: "landing_page_clicks",
        text: "Landing Page Clicks",
        style: { width: "150px", minWidth: "150px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(
                row["landing_page_clicks"],
                null,
                null,
                row.formatExtraData
              )}
            </div>
          ));
        },
      },
      {
        dataField: "cost",
        text: "Cost",
        style: { width: "125px", minWidth: "125px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "currency" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(row["cost"], null, null, row.formatExtraData)}
            </div>
          ));
        },
      },
      {
        dataField: "cpm",
        text: "CPM",
        style: { width: "125px", minWidth: "125px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "currency", format: "$0.000a" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(row["cpm"], null, null, row.formatExtraData)}
            </div>
          ));
        },
      },
      {
        dataField: "cpc",
        text: "CPC",
        style: { width: "125px", minWidth: "125px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "currency", format: "$0.000a" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(row["cpc"], null, null, row.formatExtraData)}
            </div>
          ));
        },
      },
      {
        dataField: "cpv",
        text: "CPV",
        style: { width: "125px", minWidth: "125px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "currency", format: "$0.000a" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(row["cpv"], null, null, row.formatExtraData)}
            </div>
          ));
        },
      },
      {
        dataField: "cpa",
        text: "CPA",
        style: { width: "125px", minWidth: "125px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "currency", format: "$0.000a" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(row["cpa"], null, null, row.formatExtraData)}
            </div>
          ));
        },
      },
      {
        dataField: "total_purchases",
        text: "Total Purchases",
        style: { width: "120px", minWidth: "120px" },
        headerAlign: "left",
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(row["total_purchases"])}
            </div>
          ));
        },
      },
      {
        dataField: "total_subscribers",
        text: "Total Subscribers",
        style: { width: "120px", minWidth: "120px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) => selectFormat("number", cell && cell.value),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(
                row["total_subscribers"],
                null,
                null,
                row.formatExtraData
              )}
            </div>
          ));
        },
      },
      {
        dataField: "total_subs",
        text: "Total Subscriptions",
        style: { width: "130px", minWidth: "130px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) => selectFormat("number", cell && cell.value),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(
                row["total_subs"],
                null,
                null,
                row.formatExtraData
              )}
            </div>
          ));
        },
      },
      {
        dataField: "cpts",
        text: "CPTS",
        style: { width: "90px", minWidth: "90px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "currency" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(row["cpts"], null, null, row.formatExtraData)}
            </div>
          ));
        },
      },
      {
        dataField: "cptp",
        text: "CPTP",
        style: { width: "90px", minWidth: "90px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "currency" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(row["cptp"], null, null, row.formatExtraData)}
            </div>
          ));
        },
      },
      {
        dataField: "cplv",
        text: "CPLV",
        style: { width: "125px", minWidth: "125px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "currency", format: "$0.000a" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(row["cplv"], null, null, row.formatExtraData)}
            </div>
          ));
        },
      },
      {
        dataField: "ctr",
        text: "CTR",
        style: { width: "125px", minWidth: "125px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "percentage", format: "%0.00" },
        csvFormatter: (cell) =>
          selectFormat("percentage", cell && cell.value, "%0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(row["ctr"], null, null, row.formatExtraData)}
            </div>
          ));
        },
      },
      {
        dataField: "yesterday",
        text: "Yesterday",
        style: { width: "120px", minWidth: "120px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(
                row["yesterday"],
                null,
                null,
                row.formatExtraData
              )}
            </div>
          ));
        },
      },
      {
        dataField: "percentage",
        text: "Percentage",
        style: { width: "120px", minWidth: "120px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "percentage", format: "0.0%" },
        csvFormatter: (cell) =>
          selectFormat("percentage", cell && cell.value, "0.0%"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(
                row["percentage"],
                null,
                null,
                row.formatExtraData
              )}
            </div>
          ));
        },
      },
      {
        dataField: "vs_last_year",
        text: "vs Last Year",
        style: { width: "120px", minWidth: "120px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(
                row["vs_last_year"],
                null,
                null,
                row.formatExtraData
              )}
            </div>
          ));
        },
      },
      {
        dataField: "vs_2023",
        text: "vs 2023",
        style: { width: "120px", minWidth: "120px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(
                row["vs_2023"],
                null,
                null,
                row.formatExtraData
              )}
            </div>
          ));
        },
      },
      {
        dataField: "view_thru",
        text: "View Through",
        style: { width: "120px", minWidth: "120px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) => selectFormat("number", cell && cell.value),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(
                row["view_thru"],
                null,
                null,
                row.formatExtraData
              )}
            </div>
          ));
        },
      },
      {
        dataField: "cpvv",
        text: "CPVV",
        style: { width: "125px", minWidth: "125px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "currency", format: "$0.000a" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(row["cpvv"], null, null, row.formatExtraData)}
            </div>
          ));
        },
      },
      {
        dataField: "cpa_new_sub_starts",
        text: "CPA New Sub Starts",
        style: { width: "150px", minWidth: "150px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "currency", format: "$0.000a" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(
                row["cpa_new_sub_starts"],
                null,
                null,
                row.formatExtraData
              )}
            </div>
          ));
        },
      },
      {
        dataField: "acquisition_spend",
        text: "Acquisition Spend",
        style: { width: "150px", minWidth: "150px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "currency" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(
                row["acquisition_spend"],
                null,
                null,
                row.formatExtraData
              )}
            </div>
          ));
        },
      },
      {
        dataField: "acq_cpa",
        text: "Acquisition CPA",
        style: { width: "150px", minWidth: "150px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "currency", format: "$0.000a" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(
                row["acq_cpa"],
                null,
                null,
                row.formatExtraData
              )}
            </div>
          ));
        },
      },
      {
        dataField: "client_cost",
        text: "Client Cost",
        style: { width: "125px", minWidth: "125px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "currency" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(
                row["client_cost"],
                null,
                null,
                row.formatExtraData
              )}
            </div>
          ));
        },
      },
      {
        dataField: "cpcv",
        text: "CPCV",
        style: { width: "125px", minWidth: "125px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "currency", format: "$0.000a" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(row["cpcv"], null, null, row.formatExtraData)}
            </div>
          ));
        },
      },
      {
        dataField: "agency_cost",
        text: "Agency Cost",
        style: { width: "125px", minWidth: "125px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "currency" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(
                row["agency_cost"],
                null,
                null,
                row.formatExtraData
              )}
            </div>
          ));
        },
      },
      {
        dataField: "views_25",
        text: "Views 25%",
        style: { width: "120px", minWidth: "120px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(
                row["views_25"],
                null,
                null,
                row.formatExtraData
              )}
            </div>
          ));
        },
      },
      {
        dataField: "views_50",
        text: "Views 50%",
        style: { width: "120px", minWidth: "120px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(
                row["views_50"],
                null,
                null,
                row.formatExtraData
              )}
            </div>
          ));
        },
      },
      {
        dataField: "views_75",
        text: "Views 75%",
        style: { width: "120px", minWidth: "120px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
        footer: (columnData, columnIndex) => {
          return footerRows.map((row, index) => (
            <div
              key={index}
              style={index === 0 ? footerStyles.totalRow : footerStyles.row}
            >
              {getMetricForColumn(
                row["views_75"],
                null,
                null,
                row.formatExtraData
              )}
            </div>
          ));
        },
      },
    ];

    // Modify column selection to handle name/source alias
    cells.forEach((cell, i) => {
      if (cell === "name" && !data[0].name && data[0].source) {
        // If name is requested but data has source, use source column
        selected_columns.push(columns.find((o) => o.dataField === "source"));
      } else if (cell === "source" && !data[0].source && data[0].name) {
        // If source is requested but data has name, use name column
        selected_columns.push(columns.find((o) => o.dataField === "name"));
      } else {
        // Normal case - use the requested column
        selected_columns.push(columns.find((o) => o.dataField === cell));
      }
    });

    // Only add pagination if there are more than 10 rows
    const pagination =
      table_data.length > 10
        ? paginationFactory({
            sizePerPage: 10,
            hideSizePerPage: true,
            hidePageListOnlyOnePage: true,
          })
        : undefined;

    const defaultSorted = [
      {
        dataField: "cost",
        order: "desc",
      },
    ];

    const filename = title.split(" ").join("_") || "spreadsheet";

    return (
      <ToolkitProvider
        keyField="id"
        data={table_data}
        columns={selected_columns}
        striped={false}
        hover={false}
        bordered={false}
        exportCSV={{
          fileName: filename + ".csv",
          exportAll: false,
          onlyExportFiltered: true,
        }}
      >
        {(props) => (
          <BootstrapTable
            {...props.baseProps}
            {...props.csvProps}
            pagination={pagination}
            defaultSorted={defaultSorted}
            footerClasses="tfoot__tr"
            ref={(el) => (this.childRef = el)}
            noDataIndication={() => null}
            classes="custom-table"
            headerClasses="table-header"
            bodyClasses="table-body"
            footerStyle={footerStyles.container}
          />
        )}
      </ToolkitProvider>
    );
  }

  builtInExportCSV = () => this.childRef;

  render() {
    const { data, cells } = this.props;

    if (data.hasData === false) {
      return <Error minHeight={360} />;
    }

    return this.renderTable(data.data, cells);
  }
}

export default AdsDataByNetwork;
